import { useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import Card from "@mui/material/Card"
import CircularProgress from "@mui/material/CircularProgress"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import config from "config"
import { jwtDecode } from "jwt-decode"
import { withBasename } from "appBasePath"

const API_KEY_ERROR = "Error de API Key no valido. Contacte con su administrador"
const EMBED_SESSION_FLAG_KEY = "embed.session.active"
const EMBED_API_KEY_STORAGE_KEY = "embed.session.apiKey"
const EMBED_PARENT_ORIGIN = config.embedParentOrigin

const notifyParent = (type, extra = {}) => {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(
      {
        source: "middleware-portal-monitoreo",
        type,
        ...extra,
      },
      EMBED_PARENT_ORIGIN
    )
  }
}

function EmbedLogin() {
  const { search } = useLocation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const apiKey = useMemo(() => {
    const params = new URLSearchParams(search)
    return params.get("apikey")?.trim() || params.get("apiKey")?.trim() || ""
  }, [search])

  useEffect(() => {
    let cancelled = false

    const loginWithApiKey = async () => {
      if (!apiKey) {
        setError(API_KEY_ERROR)
        setLoading(false)
        notifyParent("PORTAL_EMBED_AUTH_INVALID")
        return
      }

      try {
        const response = await fetch(`${config.api.baseURL}/auth/token-login`, {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token: apiKey }),
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        const data = await response.json()
        if (!data?.jwt) {
          throw new Error("JWT not found in response")
        }

        const decoded = jwtDecode(data.jwt)
        // Use sessionStorage for embed mode: localStorage is blocked in cross-origin iframes
        // on Safari and Firefox. App.js already reads sessionStorage("token") first.
        sessionStorage.setItem("token", data.jwt)
        sessionStorage.setItem("role", decoded?.role || "")
        sessionStorage.setItem("username", decoded?.sub || "")
        sessionStorage.setItem("company", decoded?.company || "")
        sessionStorage.setItem("companyId", decoded?.companyId || "")
        sessionStorage.setItem(EMBED_SESSION_FLAG_KEY, "1")
        sessionStorage.setItem(EMBED_API_KEY_STORAGE_KEY, apiKey)

        if (!cancelled) {
          // No dispatching auth:login here — window.location.href causes a full page
          // reload so App.js will re-read the token from sessionStorage on its own.
          // Dispatching the event would trigger a re-render on this page (routesReady=false),
          // which unmounts EmbedLogin and can set cancelled=true before navigation completes.
          notifyParent("PORTAL_EMBED_AUTH_OK")
          window.location.href = withBasename("/dashboard")
        }
      } catch {
        if (!cancelled) {
          sessionStorage.removeItem("token")
          sessionStorage.removeItem("role")
          sessionStorage.removeItem("username")
          sessionStorage.removeItem("company")
          sessionStorage.removeItem("companyId")
          sessionStorage.removeItem(EMBED_SESSION_FLAG_KEY)
          sessionStorage.removeItem(EMBED_API_KEY_STORAGE_KEY)
          notifyParent("PORTAL_EMBED_AUTH_INVALID")
          setError(API_KEY_ERROR)
          setLoading(false)
        }
      }
    }

    loginWithApiKey()

    return () => {
      cancelled = true
    }
  }, [apiKey])

  return (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      px={2}
      sx={{ backgroundColor: "#f4f7fa" }}
    >
      <Card sx={{ width: "100%", maxWidth: 520, p: 4, textAlign: "center" }}>
        {loading ? (
          <>
            <CircularProgress color="primary" size={56} />
            <MDTypography variant="h6" mt={2}>
              Validando API Key...
            </MDTypography>
          </>
        ) : (
          <MDTypography variant="h6" color="error">
            {error}
          </MDTypography>
        )}
      </Card>
    </MDBox>
  )
}

export default EmbedLogin
